/** Local SVG art only. WeaponParts owns family metadata and the hand transform. */
export const SHIELD_PRIMARY_GRIP = { x: 5, y: 10 } as const;

/** Includes every shield variant; there are no outward strokes or filters. */
export const SHIELD_LOCAL_BOUNDS = { minX: -12, maxX: 18, minY: -17, maxY: 30 } as const;

interface ShieldPlate {
  kind: 'buckler' | 'heater' | 'tower';
  rim: string;
  face: string;
  shade: string;
  shine: string;
  lowerRivetY: number;
}

function plateForVariant(variant: number): ShieldPlate {
  if (variant === 0) {
    return {
      kind: 'buckler',
      rim: 'M3,-12 A15,15 0 1 1 3,18 A15,15 0 1 1 3,-12 Z',
      face: 'M3,-9 A12,12 0 1 1 3,15 A12,12 0 1 1 3,-9 Z',
      shade: 'M3,-9 A12,12 0 0 1 3,15 Q10,3 3,-9 Z',
      shine: 'M-7,1 Q-6,-7 2,-8 L1,-5 Q-4,-4 -5,2 Z',
      lowerRivetY: 15.5,
    };
  }
  if (variant === 2) {
    return {
      kind: 'tower',
      rim: 'M-10,-16 L15,-16 L18,23 L10,30 L-7,29 L-12,21 Z',
      face: 'M-7,-13 L12,-13 L15,21 L9,27 L-5,26 L-9,20 Z',
      shade: 'M3,-13 L12,-13 L15,21 L9,27 L3,26 Z',
      shine: 'M-6,-12 L1,-12 L1,-9 L-4,-9 L-5,18 L-7,17 Z',
      lowerRivetY: 27.5,
    };
  }
  return {
    kind: 'heater',
    rim: 'M-10,-14 L3,-17 L16,-13 L18,5 Q17,20 4,30 Q-10,21 -12,6 L-12,-6 Z',
    face: 'M-8,-11 L3,-14 L13,-10 L15,5 Q14,18 4,26 Q-7,19 -9,5 L-9,-5 Z',
    shade: 'M3,-14 L13,-10 L15,5 Q14,18 4,26 L3,13 Z',
    shine: 'M-7,-10 L2,-13 L1,-10 L-4,-8 L-5,5 Q-4,13 0,18 L-4,14 Q-8,8 -7,-10 Z',
    lowerRivetY: 26.5,
  };
}

/**
 * Variant 0 is a buckler, 2 a tower; other variants use a heater silhouette.
 * The leather carrying loop passes through (5,10). Keep this fragment below
 * the existing front hand/glove: the hand closes over the middle of the loop,
 * leaving its riveted ends visible. No off-hand or body-rig changes are needed.
 */
export function renderShieldArtwork(
  primaryColor: string,
  accentColor: string = primaryColor,
  variant = 1,
): string {
  const plate = plateForVariant(variant);
  return `<g data-weapon-art="shield" data-shield-shape="${plate.kind}">
    <path d="${plate.rim}" fill="${accentColor}"/>
    <path d="${plate.face}" fill="${primaryColor}"/>
    <path d="${plate.shade}" fill="#101820" opacity="0.24"/>
    <path d="${plate.shine}" fill="#ffffff" opacity="0.24"/>
    <!-- Riveted rim and raised metal boss retain their relief at sprite scale. -->
    <circle cx="-8" cy="-6" r="1.4" fill="#101820" opacity="0.45"/>
    <circle cx="13" cy="-6" r="1.4" fill="#101820" opacity="0.45"/>
    <circle cx="3" cy="${plate.lowerRivetY}" r="1.2" fill="#101820" opacity="0.45"/>
    <circle cx="-8.3" cy="-6.4" r="0.55" fill="#ffffff" opacity="0.65"/>
    <circle cx="12.7" cy="-6.4" r="0.55" fill="#ffffff" opacity="0.65"/>
    <circle cx="1" cy="-2" r="6.2" fill="#101820" opacity="0.55"/>
    <circle cx="1" cy="-2.5" r="5.5" fill="${accentColor}"/>
    <path d="M-3.5,-1 Q1,4 5.5,-1 Q4.8,3 1,3 Q-2.8,3 -3.5,-1 Z" fill="#101820" opacity="0.32"/>
    <ellipse cx="-0.5" cy="-4.2" rx="2.4" ry="1.3" fill="#ffffff" opacity="0.5"/>
    <!-- The carrying loop meets the canonical grip, not the boss decoration. -->
    <path data-shield-carrying-loop="front-hand" d="M0,4 Q5,1 10,5 L11,14 Q6,18 1,15 Z" fill="#392a24"/>
    <path d="M2,5 Q5,3.5 8,6 L9,13 Q6,15 3,13.5 Z" fill="#8b6340"/>
    <circle cx="1.5" cy="4.7" r="1" fill="${accentColor}"/>
    <circle cx="9.5" cy="14" r="1" fill="${accentColor}"/>
    <circle data-shield-grip="front-hand" cx="5" cy="10" r="0"/>
  </g>`;
}
